import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import { projectsApi } from '../services/api'
import { ArrowLeftIcon, FileIcon, ArrowRightIcon } from '../components/Icons'
import '../styles/ProjectDetail.css'

function ProjectDetail() {
  const { id } = useParams()
  const [project, setProject] = useState(null)
  const [relatedProjects, setRelatedProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await projectsApi.getById(id)
        setProject(response.data)

        // Fetch related projects
        if (response.data.related_projects && response.data.related_projects.length > 0) {
          const relatedPromises = response.data.related_projects.map(projectId =>
            projectsApi.getById(projectId).catch(() => null)
          )
          const relatedData = await Promise.all(relatedPromises)
          setRelatedProjects(relatedData.filter(p => p !== null).map(r => r.data))
        }
      } catch (err) {
        setError('Project not found')
        console.error('Error fetching project:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchProject()
  }, [id])

  if (loading) {
    return <div className="loading">Loading project...</div>
  }

  if (error || !project) {
    return (
      <div className="error-page">
        <h1>Project Not Found</h1>
        <p>The project you're looking for doesn't exist.</p>
        <Link to="/projects" className="btn btn-primary">
          Back to Projects
        </Link>
      </div>
    )
  }

  const heroImage = project.images && project.images.length > 0 ? project.images[0] : null
  const galleryImages = project.images && project.images.length > 1 ? project.images.slice(1) : []

  return (
    <div className="project-detail">
      {/* Hero Section with Image Background */}
      <div
        className="project-hero"
        style={{
          backgroundImage: heroImage ? `url(${heroImage})` : 'none',
          backgroundColor: heroImage ? 'transparent' : '#1f2937'
        }}
      >
        <div className="hero-content">
          <Link to="/projects" className="back-link">
            <ArrowLeftIcon size={16} /> Back to Projects
          </Link>
          <h1 className="hero-title">{project.title}</h1>
          <div className="project-meta">
            <span className="category-badge">{project.category.replace('-', ' ')}</span>
            <span className="date">
              {format(new Date(project.created_at), 'MMMM d, yyyy')}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="project-body">
        <div className="project-description">
          <p>{project.description}</p>
        </div>

        {project.content && (
          <div className="project-content">
            <ReactMarkdown>{project.content}</ReactMarkdown>
          </div>
        )}

        {/* Gallery Images (excluding hero) */}
        {galleryImages.length > 0 && project.image_display_mode === 'gallery' && (
          <div className="project-gallery">
            <h2>Gallery</h2>
            <div className="gallery-grid">
              {galleryImages.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${project.title} - Image ${index + 2}`}
                  className="gallery-image"
                />
              ))}
            </div>
          </div>
        )}

        {project.files && project.files.length > 0 && (
          <div className="project-files">
            <h2>Downloads</h2>
            <div className="files-list">
              {project.files.map((file, index) => (
                <a
                  key={index}
                  href={file.url}
                  download
                  className="file-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="file-icon"><FileIcon size={18} /></span>
                  <span className="file-name">{file.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {project.tags && project.tags.length > 0 && (
          <div className="project-tags">
            {project.tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Related / Follow-on Projects */}
      {relatedProjects.length > 0 && (
        <div className="related-projects">
          <div className="related-header">
            <h2>Related & Follow-on Projects</h2>
            <p>Continue exploring this project series</p>
          </div>
          <div className="related-grid">
            {relatedProjects.map((relatedProject) => (
              <Link
                key={relatedProject.id}
                to={`/projects/${relatedProject.id}`}
                className="related-card"
              >
                {relatedProject.images && relatedProject.images[0] && (
                  <div className="related-image">
                    <img src={relatedProject.images[0]} alt={relatedProject.title} />
                  </div>
                )}
                <div className="related-info">
                  <h3>{relatedProject.title}</h3>
                  <p>{relatedProject.description}</p>
                  <span className="read-more">View Project <ArrowRightIcon size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectDetail
